import type { CSSProperties, ComponentPropsWithRef, ReactNode } from "react";
import { forwardRef } from "react";
import { omit } from "lodash";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useAttachmentMetadata } from "@circle-react-shared/BuilderUI/Media/AttachmentContext";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseSizing } from "@circle-react-shared/BuilderUI/utils/parseSizing";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import { defaultStyles } from "./defaultStyles";

interface StylesBackgroundImageValue {
  placeholder: string;
  signed_id: string;
}

interface SectionProps extends Omit<ComponentPropsWithRef<"div">, "style"> {
  ariaLabel?: string;
  children: ReactNode;
  role?: string;
  styles?: Readonly<Record<string, StyleValue>>;
}

export const Section = forwardRef<HTMLDivElement, SectionProps>(
  ({ children, styles = {}, ariaLabel, role, className, ...rest }, ref) => {
    const { backgroundImage } = styles;
    const { value } = backgroundImage || {};
    const { signed_id: backgroundImageSignedId, placeholder } =
      (value as StylesBackgroundImageValue) || {
        signed_id: "",
        placeholder: "",
      };

    const attachmentMetadata = useAttachmentMetadata(backgroundImageSignedId);
    const { url: attachmentUrl } = attachmentMetadata ?? {};
    const url = attachmentUrl || placeholder;

    const inlineStyles: CSSProperties = {
      ...parseStyles({
        styles: {
          backgroundImage: {
            type: "background-image",
            value: url,
          },
          ...omit(styles, ["backgroundImage", "sizing"]),
        },
        defaultStyles,
      }),
      ...parseSizing({
        sizing: styles.sizing,
        defaultSizing: defaultStyles.sizing,
      }),
    };

    return (
      <div
        ref={ref}
        className={classNames(
          "wb-flex wb-p-5 wb-w-full wb-h-auto wb-min-h-24 wb-bg-transparent",
          className,
        )}
        style={inlineStyles}
        aria-label={ariaLabel}
        role={role}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

Section.displayName = "Section";
